import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  AlertTriangle,
  Users,
  Lock,
  Eye,
  FileText,
  Phone,
  CreditCard,
  Star,
  Camera,
} from 'lucide-react';
import { SafetySkeleton } from '../../components/shared/ClientPageSkeleton';
import { siteSettingsService } from '../../services/siteSettingsService';
import { pageValue, sectionOrder, seedFromSettings } from '../../content/publicPages';
import { renderSections } from '../../content/sectionTone';
import { usePublicPage } from '../../hooks/usePublicPage';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingImagePair,
  MarketingPrimaryButton,
  MarketingSecondaryButton,
  MarketingHeroLightButton,
  MarketingGhostOnDark,
} from '../../components/home/MarketingPageChrome';

const SAFETY_HERO =
  'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1800&q=80';

const Safety = () => {
  const { saved, defaults } = usePublicPage('safety');
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const seed = seedFromSettings('safety', settingsResponse?.data || {});
  const val = (key) => pageValue(saved, seed, defaults, key);
  const icons = [Eye, Users, FileText, Shield, CreditCard, Star, Lock, Camera];
  const featureItems = (val('guidelines') || []).map((item, index) => ({
    icon: icons[index % icons.length],
    title: item.title,
    description: item.description,
  }));
  const storyBlocks = val('stories') || [];
  const introParagraphs = String(val('intro_body') || '')
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const order = sectionOrder(saved, 'safety');

  if (isLoading) {
    return <SafetySkeleton />;
  }

  const blocks = {
    hero: (
      <MarketingMediaHero
        key="hero"
        title={val('hero_title')}
        subtitle={val('hero_subtitle')}
        image={val('hero_image') || SAFETY_HERO}
        actions={
          <>
            <MarketingHeroLightButton href="#safety-features">{val('hero_primary_label')}</MarketingHeroLightButton>
            <MarketingGhostOnDark to={val('hero_secondary_link') || '/report'}>
              {val('hero_secondary_label')}
            </MarketingGhostOnDark>
          </>
        }
      />
    ),
    intro: (
      <MarketingSection key="intro" tone="white">
        <MarketingSplitIntro title={val('intro_title')}>
          {introParagraphs.map((para) => (
            <p key={para.slice(0, 24)}>{para}</p>
          ))}
        </MarketingSplitIntro>
      </MarketingSection>
    ),
    guidelines: (
      <MarketingSection key="guidelines" tone="white" id="safety-features" className="!pt-0 sm:!pt-0">
        <MarketingFeatureGrid items={featureItems} />
      </MarketingSection>
    ),
    stories: (
      <MarketingSection key="stories" tone="canvas">
        <MarketingImagePair items={storyBlocks} />
      </MarketingSection>
    ),
    emergency: (val('emergency_title') || val('emergency_body')) ? (
      <MarketingSection key="emergency" tone="white">
        <MarketingReveal className="mx-auto flex max-w-3xl items-start gap-4">
          <AlertTriangle className="mt-1 h-5 w-5 shrink-0" style={{ color: '#0a0a0a' }} />
          <div>
            <h3 className="mb-2 text-xl font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
              {val('emergency_title')}
            </h3>
            <p className="mb-6 text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
              {val('emergency_body')}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              {val('emergency_phone') ? (
                <a href={`tel:${val('emergency_phone')}`} className="marketing-btn marketing-btn-ink gap-2">
                  <Phone className="h-4 w-4" />
                  {val('emergency_phone')}
                </a>
              ) : null}
              {val('emergency_button') ? (
                <MarketingSecondaryButton to="/report">{val('emergency_button')}</MarketingSecondaryButton>
              ) : null}
            </div>
          </div>
        </MarketingReveal>
      </MarketingSection>
    ) : null,
    closing: (
      <MarketingSection key="closing" tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            {val('closing_title')}
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">{val('closing_body')}</p>
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <MarketingPrimaryButton to={val('closing_primary_link') || '/contact'} className="!bg-white !text-m-ink hover:!bg-white/90">
              {val('closing_primary_label')}
            </MarketingPrimaryButton>
            <MarketingGhostOnDark to={val('closing_secondary_link') || '/help'}>
              {val('closing_secondary_label')}
            </MarketingGhostOnDark>
          </div>
        </MarketingReveal>
      </MarketingSection>
    ),
  };

  return <MarketingPage>{renderSections(order, blocks, saved)}</MarketingPage>;
};

export default Safety;
